import { useState, useEffect } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Building2, ArrowLeft, Save, FileText, TrendingUp, DollarSign,
  Home, Percent, Calculator, BarChart3, PieChart, ChevronRight
} from "lucide-react";
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from "axios";
import { toast } from "sonner";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import AddressAutofill from "@/components/AddressAutofill";
import StressTest from "@/components/StressTest";
import { FREE_REPORT_GATE_MESSAGE, isFreeTier, normalizeTier } from "@/utils/planMessaging";
import { useReportQuota } from "@/hooks/useReportQuota";
import { formatIrr } from "@/utils/irr";

const RentalAnalysis = () => {
  const { id } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const normalizedUserTier = normalizeTier(user?.tier);
  const isFreeInvestor = isFreeTier(user?.tier);
  const {
    reportsUsedThisMonth,
    isStarter,
    isProLike,
    starterLimitReached,
    reportPlanText,
  } = useReportQuota(Boolean(user && token));
  const propertyTypeOptions = [
    { value: "single_family", label: "Single Family" },
    { value: "duplex", label: "Duplex" },
    { value: "triplex", label: "Triplex" },
    { value: "fourplex", label: "Fourplex" },
  ];
  const lockedPlanLabel = "Starter/Pro";
  
  // Get prefill data from Quick Screen if available
  const prefillData = location.state?.prefill;
  
  const [formData, setFormData] = useState({
    property_name: "",
    address: "",
    property_type: "single_family",
    num_units: 1,
    purchase_price: prefillData?.purchase_price || 300000,
    closing_costs: prefillData?.purchase_price ? Math.round(prefillData.purchase_price * 0.02) : 6000,
    rehab_costs: prefillData?.rehab_costs || 0,
    down_payment_percent: prefillData?.down_payment_percent || 20,
    loan_term_years: 30,
    interest_rate: prefillData?.interest_rate || 7,
    monthly_rent: prefillData?.monthly_rent || 2500,
    other_income: 0,
    vacancy_rate: 5,
    property_taxes_annual: prefillData?.purchase_price ? Math.round(prefillData.purchase_price * 0.012) : 3600,
    insurance_annual: 1800,
    hoa_monthly: 0,
    maintenance_percent: 5,
    capex_percent: 5,
    management_percent: 10,
    utilities_monthly: 0,
    other_expenses_monthly: 0,
    rent_growth_rate: 3,
    expense_growth_rate: 2,
    appreciation_rate: 3,
    projection_years: isFreeInvestor ? 10 : 30
  });

  useEffect(() => {
    if (id && token) {
      loadAnalysis();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  useEffect(() => {
    if (isFreeInvestor && formData.projection_years > 10) {
      setFormData(prev => ({ ...prev, projection_years: 10 }));
      setAnalysis(null);
    }
  }, [isFreeInvestor, formData.projection_years]);

  useEffect(() => {
    if (isFreeInvestor && (formData.property_type !== "single_family" || formData.num_units !== 1)) {
      setFormData(prev => ({
        ...prev,
        property_type: "single_family",
        num_units: 1,
      }));
      setAnalysis(null);
    }
  }, [isFreeInvestor, formData.property_type, formData.num_units]);

  const loadAnalysis = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/analysis/rental/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalysis(response.data);
      setFormData(response.data.inputs);
    } catch (error) {
      toast.error("Failed to load analysis");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'projection_years' && isFreeInvestor && Number(value) > 10) {
      toast.error('Free Investor plan supports up to 10-year rental projections. Upgrade to Starter or Pro for longer projections.');
      value = 10;
    }
    if (field === 'property_type' && isFreeInvestor && value !== 'single_family') {
      toast.error('Free Investor plan only supports single-family rental analysis. Upgrade to Starter or Pro for multi-unit property types.');
      value = 'single_family';
    }
    if (field === 'num_units' && isFreeInvestor && Number(value) !== 1) {
      toast.error('Free Investor plan only supports one-unit rental analysis. Upgrade to Starter or Pro for multi-unit properties.');
      value = 1;
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    setAnalysis(null); // Clear results when input changes
  };

  const sanitizeNumericField = (value, { required = false, fallback = 0, label = "a valid number" } = {}) => {
    if (value === "" || value === null || typeof value === "undefined") {
      if (required) {
        return { error: `Please enter ${label}` };
      }
      return { value: fallback };
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      if (required) {
        return { error: `Please enter ${label}` };
      }
      return { value: fallback };
    }

    return { value: parsed };
  };

  const buildAnalysisPayload = () => {
    const requiredNumericFields = [
      { key: "purchase_price", label: "purchase price" },
      { key: "monthly_rent", label: "monthly rent" },
    ];

    const optionalNumericConfig = {
      num_units: 1,
      closing_costs: 0,
      rehab_costs: 0,
      down_payment_percent: 0,
      loan_term_years: 30,
      interest_rate: 0,
      other_income: 0,
      vacancy_rate: 0,
      property_taxes_annual: 0,
      insurance_annual: 0,
      hoa_monthly: 0,
      maintenance_percent: 0,
      capex_percent: 0,
      management_percent: 0,
      utilities_monthly: 0,
      other_expenses_monthly: 0,
      rent_growth_rate: 0,
      expense_growth_rate: 0,
      appreciation_rate: 0,
      projection_years: isFreeInvestor ? 10 : 30,
    };

    for (const field of requiredNumericFields) {
      const parsed = sanitizeNumericField(formData[field.key], { required: true, label: field.label });
      if (parsed.error) {
        return { error: parsed.error };
      }
    }

    const numericPayload = {};
    for (const [field, fallback] of Object.entries(optionalNumericConfig)) {
      const parsed = sanitizeNumericField(formData[field], { fallback });
      numericPayload[field] = parsed.value;
    }

    numericPayload.num_units = Math.max(1, Math.round(numericPayload.num_units));
    numericPayload.loan_term_years = Math.max(1, Math.round(numericPayload.loan_term_years));
    numericPayload.projection_years = Math.max(1, Math.round(numericPayload.projection_years));

    return {
      value: {
        ...formData,
        ...numericPayload,
      },
    };
  };

  const handleReportClick = () => {
    if (isFreeInvestor) {
      toast.error(FREE_REPORT_GATE_MESSAGE);
      return;
    }
    if (starterLimitReached) {
      toast.error("Monthly report limit reached. Upgrade to Pro for unlimited reports.");
      return;
    }
    navigate(`/report/rental/${analysis.id}`);
  };

  const calculateAnalysis = async () => {
    if (!formData.property_name) {
      toast.error("Please enter a property name");
      return;
    }

    const sanitizedPayload = buildAnalysisPayload();
    if (sanitizedPayload.error) {
      toast.error(sanitizedPayload.error);
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/analysis/rental`, sanitizedPayload.value, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalysis(response.data);
      if (response.data?.inputs) {
        setFormData(response.data.inputs);
      }
      toast.success("Analysis calculated and saved!");
      navigate(`/rental-analysis/${response.data.id}`, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to calculate analysis");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(2)}%`;
  };

  // Handle auto-fill from market data
  const handleMarketDataFetched = (data) => {
    setFormData(prev => ({
      ...prev,
      address: data.address || prev.address,
      purchase_price: data.estimated_value || prev.purchase_price,
      monthly_rent: data.estimated_rent || prev.monthly_rent,
    }));
    toast.success('Form auto-filled with market data! Review and adjust as needed.');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900" data-testid="rental-analysis-page">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate("/dashboard")}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <h1 className="text-lg font-semibold text-white font-['Manrope']">Rental Analysis</h1>
            </div>
            <div className="flex items-center gap-3">
              <ChatToggleButton />
              {analysis && (
                <Button 
                  variant="outline"
                  onClick={handleReportClick}
                  disabled={starterLimitReached}
                  className="border-slate-700 text-slate-300 hover:text-white"
                  data-testid="view-report-btn"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Report
                </Button>
              )}
              {analysis && (
                <div className="hidden md:flex flex-col leading-tight">
                  <span className="text-xs text-slate-300">{reportPlanText}</span>
                  {isStarter && (
                    <span className="text-xs text-slate-500">{reportsUsedThisMonth} of 5 reports used this month</span>
                  )}
                  {isProLike && (
                    <span className="text-xs text-slate-500">Unlimited report generation</span>
                  )}
                </div>
              )}
              {analysis && starterLimitReached && (
                <Button
                  size="sm"
                  onClick={() => navigate('/upgrade')}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                >
                  Upgrade to Pro
                </Button>
              )}
              <Button 
                onClick={calculateAnalysis}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                data-testid="calculate-btn"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-slate-900 mr-2"></div>
                ) : (
                  <Calculator className="h-4 w-4 mr-2" />
                )}
                Calculate
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Input Form */}
          <div className="lg:col-span-5">
            <div className="glass-card rounded-xl p-6 sticky top-24">
              <Tabs defaultValue="property" className="w-full">
                <TabsList className="grid grid-cols-4 bg-slate-950 mb-6">
                  <TabsTrigger value="property" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
                    <Home className="h-4 w-4" />
                  </TabsTrigger>
                  <TabsTrigger value="financing" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
                    <DollarSign className="h-4 w-4" />
                  </TabsTrigger>
                  <TabsTrigger value="expenses" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
                    <Percent className="h-4 w-4" />
                  </TabsTrigger>
                  <TabsTrigger value="projections" className="data-[state=active]:bg-amber-500 data-[state=active]:text-slate-900">
                    <TrendingUp className="h-4 w-4" />
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="property" className="space-y-4">
                  <h3 className="font-semibold text-white mb-4">Property Details</h3>
                  
                  {/* Auto-Fill Magic */}
                  <AddressAutofill 
                    token={token} 
                    onDataFetched={handleMarketDataFetched}
                    initialAddress={formData.address}
                    userTier={normalizedUserTier}
                  />
                  
                  <div className="space-y-4 pt-2">
                    <div>
                      <Label className="text-slate-300">Property Name *</Label>
                      <Input
                        value={formData.property_name}
                        onChange={(e) => handleInputChange('property_name', e.target.value)}
                        placeholder="123 Main Street"
                        className="bg-slate-950 border-slate-800 text-white"
                        data-testid="property-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Property Type</Label>
                      <Select 
                        value={formData.property_type} 
                        onValueChange={(v) => handleInputChange('property_type', v)}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800">
                          {propertyTypeOptions.map((option) => {
                            const disabled = isFreeInvestor && option.value !== "single_family";
                            return (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                                disabled={disabled}
                                className={disabled ? "text-slate-600 opacity-50 cursor-not-allowed" : ""}
                              >
                                {option.label}{disabled ? ` - ${lockedPlanLabel}` : ""}
                              </SelectItem>
                            );
                          })}
                          {normalizedUserTier === 'pro' && (
                            <SelectItem value="multifamily">Multi-Family (5+)</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Number of Units</Label>
                      <Input
                        type="number"
                        value={formData.num_units}
                        onChange={(e) => handleInputChange('num_units', e.target.value)}
                        min={1}
                        max={isFreeInvestor ? 1 : (normalizedUserTier === 'pro' || normalizedUserTier === 'developer' ? 100 : 4)}
                        disabled={isFreeInvestor}
                        className="bg-slate-950 border-slate-800 text-white"
                      />
                      {isFreeInvestor && (
                        <p className="text-xs text-slate-500 mt-2">
                          1 unit included. Unlock more units with Starter/Pro.
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-slate-300">Purchase Price</Label>
                      <Input
                        type="number"
                        value={formData.purchase_price}
                        onChange={(e) => handleInputChange('purchase_price', e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                        data-testid="purchase-price-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Monthly Rent (Total)</Label>
                      <Input
                        type="number"
                        value={formData.monthly_rent}
                        onChange={(e) => handleInputChange('monthly_rent', e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                        data-testid="monthly-rent-input"
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="financing" className="space-y-4">
                  <h3 className="font-semibold text-white mb-4">Financing</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-300">Down Payment (%)</Label>
                      <Input
                        type="number"
                        value={formData.down_payment_percent}
                        onChange={(e) => handleInputChange('down_payment_percent', e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Interest Rate (%)</Label>
                      <Input
                        type="number"
                        step="0.125"
                        value={formData.interest_rate}
                        onChange={(e) => handleInputChange('interest_rate', e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Loan Term (Years)</Label>
                      <Select 
                        value={formData.loan_term_years.toString()} 
                        onValueChange={(v) => handleInputChange('loan_term_years', parseInt(v))}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
                          <SelectItem value="10">10 Years</SelectItem>
                          <SelectItem value="15">15 Years</SelectItem>
                          <SelectItem value="20">20 Years</SelectItem>
                          <SelectItem value="25">25 Years</SelectItem>
                          <SelectItem value="30">30 Years</SelectItem>
                          <SelectItem value="35">35 Years</SelectItem>
                          <SelectItem value="40">40 Years</SelectItem>
                          <SelectItem value="50">50 Years</SelectItem>
                          <SelectItem value="75">75 Years</SelectItem>
                          <SelectItem value="100">100 Years</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300">Closing Costs</Label>
                      <Input
                        type="number"
                        value={formData.closing_costs}
                        onChange={(e) => handleInputChange('closing_costs', e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Rehab Costs</Label>
                      <Input
                        type="number"
                        value={formData.rehab_costs}
                        onChange={(e) => handleInputChange('rehab_costs', e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="expenses" className="space-y-4">
                  <h3 className="font-semibold text-white mb-4">Operating Expenses</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-300">Vacancy Rate (%)</Label>
                      <Input
                        type="number"
                        value={formData.vacancy_rate}
                        onChange={(e) => handleInputChange('vacancy_rate', e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Property Taxes (Annual)</Label>
                      <Input
                        type="number"
                        value={formData.property_taxes_annual}
                        onChange={(e) => handleInputChange('property_taxes_annual', e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Insurance (Annual)</Label>
                      <Input
                        type="number"
                        value={formData.insurance_annual}
                        onChange={(e) => handleInputChange('insurance_annual', e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">HOA (Monthly)</Label>
                      <Input
                        type="number"
                        value={formData.hoa_monthly}
                        onChange={(e) => handleInputChange('hoa_monthly', e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Maintenance (%)</Label>
                        <Input
                          type="number"
                          value={formData.maintenance_percent}
                          onChange={(e) => handleInputChange('maintenance_percent', e.target.value)}
                          className="bg-slate-950 border-slate-800 text-white font-mono"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">CapEx (%)</Label>
                        <Input
                          type="number"
                          value={formData.capex_percent}
                          onChange={(e) => handleInputChange('capex_percent', e.target.value)}
                          className="bg-slate-950 border-slate-800 text-white font-mono"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-300">Management (%)</Label>
                      <Input
                        type="number"
                        value={formData.management_percent}
                        onChange={(e) => handleInputChange('management_percent', e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="projections" className="space-y-4">
                  <h3 className="font-semibold text-white mb-4">Growth Assumptions</h3>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-300">Rent Growth Rate (%/year)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.rent_growth_rate}
                        onChange={(e) => handleInputChange('rent_growth_rate', e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Expense Growth Rate (%/year)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.expense_growth_rate}
                        onChange={(e) => handleInputChange('expense_growth_rate', e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Appreciation Rate (%/year)</Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={formData.appreciation_rate}
                        onChange={(e) => handleInputChange('appreciation_rate', e.target.value)}
                        className="bg-slate-950 border-slate-800 text-white font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">Projection Period (Years)</Label>
                      <Select 
                        value={formData.projection_years.toString()} 
                        onValueChange={(v) => handleInputChange('projection_years', parseInt(v))}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-800 max-h-60">
                          <SelectItem value="5">5 Years</SelectItem>
                          <SelectItem value="10">10 Years</SelectItem>
                          {[15, 20, 25, 30, 40, 50, 75, 100].map((years) => (
                            <SelectItem
                              key={years}
                              value={years.toString()}
                              disabled={isFreeInvestor}
                              className={isFreeInvestor ? "text-slate-600 cursor-not-allowed opacity-50" : ""}
                            >
                              {years} Years{isFreeInvestor ? ` - ${lockedPlanLabel}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {isFreeInvestor && (
                        <p className="text-xs text-slate-500 mt-2">
                          Free Investor includes 5 and 10-year projections. Upgrade to Starter/Pro for 15+ years.
                        </p>
                      )}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Results */}
          <div className="lg:col-span-7 space-y-6">
            {!analysis ? (
              <div className="glass-card rounded-xl p-12 text-center">
                <Calculator className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Enter Property Details</h3>
                <p className="text-slate-400 mb-6">Fill in the form and click Calculate to see your analysis</p>
                <Button 
                  onClick={calculateAnalysis}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                >
                  <Calculator className="h-4 w-4 mr-2" />
                  Calculate Now
                </Button>
              </div>
            ) : (
              <>
                {/* Key Metrics */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="metric-card" data-testid="monthly-cashflow-metric">
                    <div className="text-sm text-slate-400 mb-1">Monthly Cash Flow</div>
                    <div className={`text-2xl font-bold font-mono ${analysis.results.monthly_cash_flow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {formatCurrency(analysis.results.monthly_cash_flow)}
                    </div>
                  </div>
                  <div className="metric-card" data-testid="cap-rate-metric">
                    <div className="text-sm text-slate-400 mb-1">Cap Rate</div>
                    <div className="text-2xl font-bold font-mono text-cyan-500">
                      {formatPercent(analysis.results.cap_rate)}
                    </div>
                  </div>
                  <div className="metric-card" data-testid="coc-metric">
                    <div className="text-sm text-slate-400 mb-1">Cash-on-Cash</div>
                    <div className={`text-2xl font-bold font-mono ${analysis.results.cash_on_cash_return >= 0 ? 'text-amber-500' : 'text-red-500'}`}>
                      {formatPercent(analysis.results.cash_on_cash_return)}
                    </div>
                  </div>
                  <div className="metric-card" data-testid="irr-metric">
                    <div className="text-sm text-slate-400 mb-1">IRR</div>
                    <div className="text-2xl font-bold font-mono text-purple-500">
                      {formatIrr(analysis.results.irr)}
                    </div>
                  </div>
                </div>

                {/* Stress Test Panel */}
                <StressTest inputs={formData} baseAnalysis={analysis} />

                {/* Financial Summary */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Financial Summary</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Purchase Price</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.purchase_price)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Down Payment</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.down_payment)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Loan Amount</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.loan_amount)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Total Investment</span>
                        <span className="font-mono text-amber-500 font-semibold">{formatCurrency(analysis.results.total_investment)}</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Monthly Mortgage</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.monthly_mortgage)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Monthly NOI</span>
                        <span className="font-mono text-white">{formatCurrency(analysis.results.noi / 12)}</span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">DSCR</span>
                        <span className={`font-mono ${analysis.results.dscr >= 1.25 ? 'text-emerald-500' : analysis.results.dscr >= 1 ? 'text-amber-500' : 'text-red-500'}`}>
                          {analysis.results.dscr.toFixed(2)}x
                        </span>
                      </div>
                      <div className="flex justify-between py-2 border-b border-slate-800">
                        <span className="text-slate-400">Annual Cash Flow</span>
                        <span className={`font-mono font-semibold ${analysis.results.annual_cash_flow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {formatCurrency(analysis.results.annual_cash_flow)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cash Flow Chart */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Cash Flow Projection</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={analysis.projections}>
                        <defs>
                          <linearGradient id="cashFlowGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="year" stroke="#64748b" />
                        <YAxis stroke="#64748b" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                          labelStyle={{ color: '#f1f5f9' }}
                          formatter={(value) => [formatCurrency(value), 'Cash Flow']}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="cash_flow" 
                          stroke="#10b981" 
                          fill="url(#cashFlowGradient)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Equity & Value Chart */}
                <div className="glass-card rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Property Value & Equity</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={analysis.projections}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="year" stroke="#64748b" />
                        <YAxis stroke="#64748b" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                          labelStyle={{ color: '#f1f5f9' }}
                          formatter={(value) => formatCurrency(value)}
                        />
                        <Legend />
                        <Line type="monotone" dataKey="property_value" name="Property Value" stroke="#F59E0B" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="equity" name="Equity" stroke="#06b6d4" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="loan_balance" name="Loan Balance" stroke="#ef4444" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Projections Table */}
                <div className="glass-card rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-slate-800">
                    <h3 className="text-lg font-semibold text-white">Year-by-Year Projections</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full data-table">
                      <thead>
                        <tr>
                          <th>Year</th>
                          <th>Rent</th>
                          <th>NOI</th>
                          <th>Cash Flow</th>
                          <th>Value</th>
                          <th>Equity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysis.projections.map((proj, index) => (
                          <tr key={index}>
                            <td>{proj.year}</td>
                            <td className="font-mono">{formatCurrency(proj.monthly_rent)}/mo</td>
                            <td className="font-mono">{formatCurrency(proj.noi)}</td>
                            <td className={`font-mono ${proj.cash_flow >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                              {formatCurrency(proj.cash_flow)}
                            </td>
                            <td className="font-mono">{formatCurrency(proj.property_value)}</td>
                            <td className="font-mono text-cyan-500">{formatCurrency(proj.equity)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default RentalAnalysis;
